import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "optionsPanel",
    "documentBoundingBoxes",
    "documentSearchQuery",
    "documentSearchResultCount",
    "resultsContainer",
    "pages",
    "zoomLevel",
    "zoomIn",
    "zoomOut"
  ]

  // Zoom for a record shown as page images, as a percentage of the pane's width.
  ZOOM_MIN = 50
  ZOOM_MAX = 300
  ZOOM_STEP = 25

  SELECTED_PAGE_CLASSES = ["border-2", "border-orange", "border-solid"]
  SELECTED_BOUNDING_BOX_CLASSES = ["border-2", "border-medium-gray", "bg-orange", "opacity-50"]

  initialize() {
    this.selectedBoundingBoxes = []
  }

  // A record in several files is read one file at a time (the "Showing" dropdown re-renders this
  // panel), so the zoom chosen for one carries to the next, for as long as the tab is open.
  pagesTargetConnected() {
    const remembered = parseFloat(this.zoomStorage("getItem"))
    if (Number.isFinite(remembered) && remembered !== this.zoom) this.setZoom(remembered)
  }

  // The level is what the page says it is: a page Turbo restores from its cache comes back with
  // the width it was left with, not with this controller's memory.
  get zoom() {
    return (this.hasPagesTarget && parseFloat(this.pagesTarget.style.width)) || 100
  }

  zoomIn() {
    this.setZoom(this.zoom + this.ZOOM_STEP)
  }

  zoomOut() {
    this.setZoom(this.zoom - this.ZOOM_STEP)
  }

  zoomReset() {
    this.setZoom(100)
  }

  // Resizes the pages and keeps the reader's place: what was in the middle of the pane before is
  // in the middle after.
  setZoom(level) {
    if (!this.hasPagesTarget) return

    const pane = this.resultsContainerTarget
    const down = pane.scrollHeight > 0 ? (pane.scrollTop + pane.clientHeight / 2) / pane.scrollHeight : 0
    const across = pane.scrollWidth > 0 ? (pane.scrollLeft + pane.clientWidth / 2) / pane.scrollWidth : 0

    const zoom = Math.min(this.ZOOM_MAX, Math.max(this.ZOOM_MIN, level))
    this.pagesTarget.style.width = `${zoom}%`
    this.zoomStorage("setItem", String(zoom))

    pane.scrollTop = down * pane.scrollHeight - pane.clientHeight / 2
    pane.scrollLeft = across * pane.scrollWidth - pane.clientWidth / 2

    if (this.hasZoomLevelTarget) this.zoomLevelTarget.textContent = `${zoom}%`
    // Marked rather than disabled: disabling the button a keyboard user is pressing drops their focus.
    this.markAtLimit(this.hasZoomOutTarget && this.zoomOutTarget, zoom <= this.ZOOM_MIN)
    this.markAtLimit(this.hasZoomInTarget && this.zoomInTarget, zoom >= this.ZOOM_MAX)
  }

  markAtLimit(button, atLimit) {
    if (!button) return
    button.setAttribute("aria-disabled", atLimit)
    button.classList.toggle("opacity-50", atLimit)
  }

  // sessionStorage can be unavailable (blocked storage); zoom still works.
  zoomStorage(method, value) {
    try {
      return window.sessionStorage[method]("coder-record-zoom", value)
    } catch {
      return null
    }
  }

  connect() {
  }

  toggleOptions() {
    this.optionsPanelTarget.classList.toggle("hidden");
  }

  searchDocument() {
    if (this.documentSearchQueryTarget.value.length > 0) {
      var newSelectedBoundingBoxes = []
      var boundingBoxes = document.querySelectorAll("[data-target-dom-id]")
      
      for (var i = 0; i < boundingBoxes.length; i++) {
        var val = boundingBoxes[i].dataset.value
        val = val.toLowerCase()
        var query = this.documentSearchQueryTarget.value.toLowerCase()
        if (val.includes(query)) {
          newSelectedBoundingBoxes.push(boundingBoxes[i])
        }
      }

      this.markSelected(newSelectedBoundingBoxes)

    } else {
      this.markSelected([])
      this.resultsContainerTarget.scrollTop = 0
    }
  }
  
  markSelected(selectedBoundingBoxes) {
    // deselect all currently selected bounding boxes and pages
    for (var i = 0; i < this.selectedBoundingBoxes.length; i++) {
      for (var j = 0; j < this.SELECTED_BOUNDING_BOX_CLASSES.length; j++) {
        this.selectedBoundingBoxes[i].classList.remove(this.SELECTED_BOUNDING_BOX_CLASSES[j])  
      }

      var pageElm = document.querySelector("#" + this.selectedBoundingBoxes[i].dataset.targetDomId)
      for (var j = 0; j < this.SELECTED_PAGE_CLASSES.length; j++) {
        pageElm.classList.remove(this.SELECTED_PAGE_CLASSES[j])
      }
    }

    // select new bounding boxes and pages
    this.selectedBoundingBoxes = []

    for (var i = 0; i < selectedBoundingBoxes.length; i++) {
      var pageElm = document.querySelector("#" + selectedBoundingBoxes[i].dataset.targetDomId)
      // The match itself, not just its page: a zoomed page is larger than the pane.
      if (i === 0) {
        selectedBoundingBoxes[i].scrollIntoView({ block: "center", inline: "center" })
      }

      for (var j = 0; j < this.SELECTED_BOUNDING_BOX_CLASSES.length; j++) {
        selectedBoundingBoxes[i].classList.add(this.SELECTED_BOUNDING_BOX_CLASSES[j])
      }
      
      for (var j = 0; j < this.SELECTED_PAGE_CLASSES.length; j++) {
        pageElm.classList.add(this.SELECTED_PAGE_CLASSES[j])
      }
      
      this.selectedBoundingBoxes.push(selectedBoundingBoxes[i])
    }

    this.documentSearchResultCountTarget.innerText = this.selectedBoundingBoxes.length
  }
}